// ============================================================
// Particle lifecycle manager — birth, sustain, death phases
// ============================================================

/**
 * Manages the three-phase lifecycle of a particle: birth, sustain, and death.
 *
 * Each particle begins with a birth phase where alpha scales from 0 to 1,
 * enters a sustain phase with full visibility, and finally enters a death
 * phase where alpha fades back to 0 before respawning.
 *
 * @example
 * ```ts
 * const life = new LifecycleState(300, 30, 20) // 300 frame life, 30 birth, 20 death
 * life.update() // advance age by 1 frame
 * const alpha = life.computeAlphaScale() // 0..1 based on current phase
 * if (life.isExpired) life.reset()
 * ```
 */
export class LifecycleState {
  age = 0

  /**
   * @param life — Total lifespan in animation frames
   * @param birthFrames — Duration of the birth (fade-in) phase
   * @param deathFrames — Duration of the death (fade-out) phase
   */
  constructor(
    public life: number,
    public birthFrames: number,
    public deathFrames: number
  ) {}

  /** Advance the particle age by one frame. */
  update(): void {
    this.age++
  }

  /** Reset age to 0, effectively restarting the lifecycle. */
  reset(): void {
    this.age = 0
  }

  /** Returns an alpha multiplier in [0, 1] based on birth / sustain / death */
  computeAlphaScale(): number {
    if (this.life <= 0) return this.birthFrames > 0 ? 0 : 1
    if (this.birthFrames > 0 && this.age < this.birthFrames) {
      const p = this.age / this.birthFrames
      return Math.sin(p * Math.PI / 2)
    }
    if (this.deathFrames > 0 && this.age > this.life - this.deathFrames) {
      const p = Math.max(0, this.age - (this.life - this.deathFrames)) / this.deathFrames
      return 1 - Math.sin(p * Math.PI / 2)
    }
    return 1
  }

  /** `true` when age has reached the total lifespan. */
  get isExpired(): boolean {
    return this.age >= this.life
  }
}
